import { useMemo } from "react";
import useFetchGraphData from "../useFetchGraphData";
import { parseLocalDate } from "../../utils";

export default function useStreamflowSeries({
  parameterid,
  locationid,
  startDate,
  endDate,
}) {
  const params = useMemo(
    () => ({
      parameterid,
      locationid,
      start_date: startDate,
      end_date: endDate,
    }),
    [parameterid, locationid, startDate, endDate]
  );

  const {
    data,
    isFetching: isLoading,
    isError,
  } = useFetchGraphData(params, "streamflow-data");

  const { seriesByLoc, unit, parameter } = useMemo(() => {
    if (!data?.length) return { seriesByLoc: {}, unit: "", parameter: "" };

    // Build a lookup per location keyed by date string
    const rawByLoc = {};
    const allDateStrs = new Set();
    data.forEach((d) => {
      const dateStr = d.rdate.slice(0, 10);
      allDateStrs.add(dateStr);
      if (!rawByLoc[d.location_name]) rawByLoc[d.location_name] = {};
      rawByLoc[d.location_name][dateStr] = {
        value: d.daily_average,
        min: d.daily_min,
        max: d.daily_max,
      };
    });

    // Sort dates and build aligned series with nulls for gaps
    const sortedDates = [...allDateStrs].sort();
    const byLoc = {};
    for (const loc of Object.keys(rawByLoc)) {
      byLoc[loc] = sortedDates.map((dateStr) => {
        const entry = rawByLoc[loc][dateStr];
        return {
          date: parseLocalDate(dateStr),
          value: entry ? entry.value : null,
          min: entry ? entry.min : null,
          max: entry ? entry.max : null,
        };
      });
    }

    return {
      seriesByLoc: byLoc,
      unit: data[0].result_unit_name,
      parameter: data[0].parameter,
    };
  }, [data]);

  return { seriesByLoc, unit, parameter, isLoading, isError };
}
